import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FileUpload,
  Form,
  FormField,
  Header,
  Input,
  Modal,
  RadioGroup,
  Select,
  SpaceBetween,
} from "@amzn/awsui-components-react";

import {
  ACCEPTED_FILE_TYPES,
  MAX_UPLOAD_BYTES,
  NAME_ALLOWED_HINT,
  NEW_SOLUTION,
  PAYLOAD_FAMILY,
  PAYLOAD_FAMILY_LABEL,
  RUN_MODE,
} from "./constants";
import { VALIDATION, blocksSubmission, validateFile, validateName } from "./validation";
import { detectFamily } from "./api";

/**
 * Upload — one form for both payload families.
 *
 * Two steps, because the second depends on the first: the file determines the
 * family, and the family determines which questions are worth asking. Nothing
 * is provisioned while the user is still answering — if they close the modal
 * here, nothing has been created.
 */
const UploadModal = ({ visible, onDismiss, onSubmit, solutions = [], currentUser = "" }) => {
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [onboardAs, setOnboardAs] = useState(null);
  const [family, setFamily] = useState(null);
  const [name, setName] = useState("");
  const [owner, setOwner] = useState(currentUser);
  const [dynamicData, setDynamicData] = useState(false);
  const [runMode, setRunMode] = useState(RUN_MODE.INDIVIDUAL);
  const [groupedWith, setGroupedWith] = useState(null);
  const [simTicket, setSimTicket] = useState("");
  const [hasParameters, setHasParameters] = useState(false);
  const [hasSampleIo, setHasSampleIo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const file = files[0] || null;
  const [snapshot, setSnapshot] = useState(null);

  // Read the file into memory as soon as it is chosen.
  //
  // A File from the picker is a *handle*: the browser re-reads the bytes at
  // PUT time and fails with ERR_UPLOAD_FILE_CHANGED if the file on disk has
  // changed since. This form holds that handle across two steps, so a zip
  // rebuilt while the user is on the name step breaks the upload. Snapshotting
  // the bytes removes that failure entirely — what is uploaded is what was
  // selected, whatever happens to the file afterwards.
  useEffect(() => {
    let cancelled = false;
    if (!file || typeof file.arrayBuffer !== "function") {
      // No eager read available — fall back to the handle, which works fine
      // as long as the file is not modified between selection and upload.
      setSnapshot(null);
      return undefined;
    }
    file
      .arrayBuffer()
      .then((buffer) => {
        if (cancelled) return;
        const blob = new Blob([buffer], { type: file.type || "application/zip" });
        // Keep the name: Vault and the backend both key off it.
        blob.name = file.name;
        setSnapshot(blob);
      })
      .catch(() => {
        // Fall back to the handle. Worse, but better than blocking the upload
        // on a browser that cannot read the file eagerly.
        if (!cancelled) setSnapshot(null);
      });
    return () => {
      cancelled = true;
    };
  }, [file]);

  const reset = () => {
    setStep(1);
    setFiles([]);
    setOnboardAs(null);
    setFamily(null);
    setName("");
    setOwner(currentUser);
    setDynamicData(false);
    setRunMode(RUN_MODE.INDIVIDUAL);
    setGroupedWith(null);
    setSimTicket("");
    setHasParameters(false);
    setHasSampleIo(false);
    setSubmitting(false);
    setSubmitError(null);
  };

  useEffect(() => {
    if (visible) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    setOwner(currentUser);
  }, [currentUser]);

  // Classify as soon as there is a file, so step 2 can ask the right questions.
  useEffect(() => {
    let cancelled = false;
    if (!file) {
      setFamily(null);
      return undefined;
    }
    detectFamily(file).then((detected) => {
      if (!cancelled) setFamily(detected);
    });
    return () => {
      cancelled = true;
    };
  }, [file]);

  const existingNames = useMemo(() => solutions.map((solution) => solution.name), [solutions]);

  const updatesSolution = useMemo(() => {
    if (!onboardAs || onboardAs.value === NEW_SOLUTION) return null;
    return solutions.find((solution) => solution.id === onboardAs.value) || null;
  }, [onboardAs, solutions]);

  const isUpdate = Boolean(updatesSolution);

  const onboardOptions = useMemo(
    () => [
      { label: "— Something new —", value: NEW_SOLUTION },
      ...solutions.map((solution) => ({
        label: solution.name,
        value: solution.id,
        description: PAYLOAD_FAMILY_LABEL[solution.family],
      })),
    ],
    [solutions]
  );

  const groupedOptions = useMemo(
    () =>
      solutions
        .filter((solution) => solution.family === PAYLOAD_FAMILY.SCRIPT)
        .map((solution) => ({ label: solution.name, value: solution.id })),
    [solutions]
  );

  const fileCheck = validateFile(file, {
    acceptedTypes: ACCEPTED_FILE_TYPES,
    maxBytes: MAX_UPLOAD_BYTES,
  });

  // An update inherits its name, so only a new solution is name-validated.
  // Validated on the trimmed value: surrounding whitespace comes from a paste
  // and is dropped on submit, so flagging it as an invalid character would be
  // an error the user cannot see. Characters *within* the name are never
  // altered — that is the rule this must not break.
  const nameCheck = isUpdate
    ? { status: VALIDATION.VALID, message: "" }
    : validateName(name.trim(), existingNames);

  const isScript = family === PAYLOAD_FAMILY.SCRIPT;

  const canContinue =
    Boolean(file) && fileCheck.status !== VALIDATION.ERROR && Boolean(onboardAs);

  const canSubmit =
    canContinue &&
    !blocksSubmission(nameCheck) &&
    (isUpdate || Boolean(owner.trim())) &&
    (!isScript || runMode !== RUN_MODE.GROUPED || Boolean(groupedWith));

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(snapshot || file, {
        name: isUpdate ? updatesSolution.name : name.trim(),
        owner: isUpdate ? updatesSolution.owner : owner.trim(),
        family,
        updatesSolutionId: isUpdate ? updatesSolution.id : null,
        dynamicDataEnabled: isScript ? false : dynamicData,
        runMode: isScript ? runMode : null,
        groupedWithId: isScript && runMode === RUN_MODE.GROUPED ? groupedWith && groupedWith.value : null,
        simTicket: isScript ? simTicket.trim() : null,
        hasParameters: isScript ? hasParameters : false,
        hasSampleIo: isScript ? hasSampleIo : false,
      });
    } catch (error) {
      setSubmitError(error && error.message ? error.message : "Upload failed. Nothing was deployed.");
      setSubmitting(false);
    }
  };

  const nameConstraint = nameCheck.status === VALIDATION.VALID && name ? "available" : NAME_ALLOWED_HINT;

  const footer = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={onDismiss} disabled={submitting}>
          Cancel
        </Button>
        {step === 2 && (
          <Button onClick={() => setStep(1)} disabled={submitting}>
            Back
          </Button>
        )}
        {step === 1 ? (
          <Button variant="primary" disabled={!canContinue} onClick={() => setStep(2)}>
            Continue
          </Button>
        ) : (
          <Button variant="primary" disabled={!canSubmit} loading={submitting} onClick={handleSubmit}>
            {isUpdate ? "Update" : "Submit"}
          </Button>
        )}
      </SpaceBetween>
    </Box>
  );

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={<Header variant="h2">{isUpdate ? `Updating ${updatesSolution.name}` : "Upload"}</Header>}
      footer={footer}
      size="medium"
      data-testid="byoc-upload-modal"
    >
      <Form>
        <SpaceBetween size="l">
          {submitError && (
            <Alert type="error" header="Upload failed">
              {submitError}
            </Alert>
          )}

          {step === 1 && (
            <>
              <FormField
                label="File"
                description={`Drop a ${ACCEPTED_FILE_TYPES.join(" or ")} here, or browse.`}
                errorText={fileCheck.status === VALIDATION.ERROR ? fileCheck.message : undefined}
              >
                <FileUpload
                  value={files}
                  onChange={({ detail }) => setFiles(detail.value)}
                  accept={ACCEPTED_FILE_TYPES.join(",")}
                  showFileSize
                  i18nStrings={{
                    uploadButtonText: () => "Choose file",
                    dropzoneText: () => "Drop your file here",
                    removeFileAriaLabel: (index) => `Remove file ${index + 1}`,
                    errorIconAriaLabel: "Error",
                  }}
                  constraintText={
                    family ? `Detected: ${PAYLOAD_FAMILY_LABEL[family]}` : undefined
                  }
                />
              </FormField>

              <FormField
                label="Onboard as"
                description="Leave on “Something new” to onboard, or pick an existing one to update it."
              >
                <Select
                  selectedOption={onboardAs}
                  onChange={({ detail }) => setOnboardAs(detail.selectedOption)}
                  options={onboardOptions}
                  filteringType="auto"
                  placeholder="Choose"
                  data-testid="byoc-onboard-as"
                />
              </FormField>
            </>
          )}

          {step === 2 && isUpdate && (
            <Alert type="info" header={`You are updating ${updatesSolution.name}`}>
              <SpaceBetween size="xxs">
                <div>Id {updatesSolution.id} — unchanged</div>
                <div>Link unchanged — anyone with it keeps access</div>
                {updatesSolution.dynamicDataEnabled && (
                  <div>Refreshing data on — your current data is preserved</div>
                )}
                {updatesSolution.family === PAYLOAD_FAMILY.SCRIPT && (
                  <div>This needs a fresh code review before it goes live again</div>
                )}
                <div>The current version will be replaced.</div>
              </SpaceBetween>
            </Alert>
          )}

          {step === 2 && !isUpdate && (
            <>
              <FormField
                label="Name"
                description={nameConstraint}
                errorText={nameCheck.status === VALIDATION.ERROR ? nameCheck.message : undefined}
                warningText={nameCheck.status === VALIDATION.WARNING ? nameCheck.message : undefined}
              >
                <Input
                  value={name}
                  onChange={({ detail }) => setName(detail.value)}
                  placeholder="sales-report"
                  data-testid="byoc-name-input"
                />
              </FormField>

              <FormField label="Owner">
                <Input
                  value={owner}
                  onChange={({ detail }) => setOwner(detail.value)}
                  data-testid="byoc-owner-input"
                />
              </FormField>
            </>
          )}

          {step === 2 && !isScript && (
            <FormField
              label="Refreshing data"
              description="A BI pipeline will keep its data current. You can switch this on later."
            >
              <Checkbox
                checked={dynamicData}
                onChange={({ detail }) => setDynamicData(detail.checked)}
                data-testid="byoc-dynamic-data-checkbox"
              >
                This app needs data that refreshes
              </Checkbox>
            </FormField>
          )}

          {step === 2 && isScript && (
            <>
              <FormField label="How should this run?">
                <RadioGroup
                  value={runMode}
                  onChange={({ detail }) => setRunMode(detail.value)}
                  items={[
                    {
                      value: RUN_MODE.INDIVIDUAL,
                      label: "On its own",
                      description: "Its own runnable surface, its own inputs, its own history.",
                    },
                    {
                      value: RUN_MODE.GROUPED,
                      label: "Grouped with an existing one",
                      description: "For scripts that belong together.",
                    },
                  ]}
                  data-testid="byoc-run-mode"
                />
              </FormField>

              {runMode === RUN_MODE.GROUPED && (
                <FormField label="Group with">
                  <Select
                    selectedOption={groupedWith}
                    onChange={({ detail }) => setGroupedWith(detail.selectedOption)}
                    options={groupedOptions}
                    placeholder="Pick one"
                    empty="No scripts onboarded yet"
                  />
                </FormField>
              )}

              <FormField
                label="SIM ticket"
                description="Used if the job errors. The ticket comes to you."
              >
                <Input value={simTicket} onChange={({ detail }) => setSimTicket(detail.value)} />
              </FormField>

              <FormField label="Parameters">
                <Checkbox
                  checked={hasParameters}
                  onChange={({ detail }) => setHasParameters(detail.checked)}
                >
                  This script takes parameters
                </Checkbox>
              </FormField>

              <FormField label="Sample inputs and outputs">
                <Checkbox
                  checked={hasSampleIo}
                  onChange={({ detail }) => setHasSampleIo(detail.checked)}
                >
                  I have sample inputs and outputs
                </Checkbox>
              </FormField>

              <Alert type="info">
                Scripts go through a code review before they run, so this takes a few days. If your
                script has a bug the ticket comes to you; if our infrastructure breaks it comes to us.
              </Alert>
            </>
          )}
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default UploadModal;
